import { CommonModule } from '@angular/common';
import {
    Component,
    forwardRef,
    Input,
    OnInit,
    Provider,
    ViewChild,
    ElementRef
  } from '@angular/core';
  import {
    ControlValueAccessor,
    FormControl,
    NG_VALUE_ACCESSOR
  } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
  
  export const FILE_INPUT_VALUE_ACCESSOR: Provider = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => FileInputComponent),
    multi: true,
  };
  
  @Component({
    selector: 'app-file-input',
    standalone: true,
    imports: [
        MatButtonModule,
        CommonModule
    ],
    template: `
      <button mat-raised-button color="primary" type="button" (click)="fileInput.click()">
        {{ buttonLabel }}
      </button>
  
      <input
        #fileInput
        type="file"
        [attr.accept]="accept"
        style="display: none;"
        (change)="onFileSelected($event)"
      />
  
      <div *ngIf="selectedFileName" class="selected-file">
        {{ selectedFileName }}
      </div>
    `,
    styles: [`
      .selected-file {
        margin-top: 8px;
        font-style: italic;
      }
    `],
    providers: [FILE_INPUT_VALUE_ACCESSOR]
  })
  export class FileInputComponent implements ControlValueAccessor, OnInit {
    /** Label for the browse button */
    @Input() buttonLabel = 'Choose File';
  
    /**
     * Accept attribute (e.g. ".png, .jpg, .pdf")  
     * to limit the file types selectable
     */
    @Input() accept: string | null = null;

    /** Reference to the hidden file input element */
    @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;
  
    /** We will store the selected file here */
    private file: File | null = null;
  
    /** A convenience property for displaying in the template */
    get selectedFileName(): string | null {
      return this.file ? this.file.name : null;
    }
  
    /** Our internal FormControl if needed (optional) */
    control = new FormControl();
  
    /**
     * ControlValueAccessor callbacks:
     * 1) onChange - called when the value changes
     * 2) onTouched - called when the control is "touched"
     */
    private onChange: (file: File | null) => void = (file) => {
      console.log('File changed', file);
      console.log('Existing file:', this.file);
    };
    private onTouched: () => void = () => {};
  
    ngOnInit(): void {
      // Optionally do some setup
    }
  
    /**
     * Allows Angular to set the value programmatically (writeValue).
     * In file inputs, we typically don't do much here,
     * because the user selects the file from their system.
     */
    writeValue(file: File | null): void {
      this.file = file;
      
      // Clear the HTML file input when the form value is reset to null
      if (file === null && this.fileInputRef?.nativeElement) {
        this.fileInputRef.nativeElement.value = '';
      }

      console.log('File changed:: writeValue', file);
    }
  
    /**
     * Save the callbacks from the forms API
     */
    registerOnChange(fn: (file: File | null) => void): void {
      this.onChange = fn;
    }
    registerOnTouched(fn: () => void): void {
      this.onTouched = fn;
    }
  
    /**
     * If you want to disable the input from the outside, you can do so here.
     * For the hidden file input, we can just keep track of the disabled state
     * or disable the button.
     */
    setDisabledState?(isDisabled: boolean): void {
      if (isDisabled) {
        this.control.disable();
      } else {
        this.control.enable();
      }
    }
  
    /**
     * Called when the user actually selects a file in the file dialog
     */
    onFileSelected(event: Event): void {
      const input = event.target as HTMLInputElement;
      if (!input.files?.length) {
        return;
      }
      this.file = input.files[0];
      // Notify the form that the value has changed
      this.onChange(this.file);
      // Mark the control as touched
      this.onTouched();
    }
  }
  